import { watch, computed, readonly, useRoute, useCoreConfig, useState, useUserInfo, useEventListener, useCookiebotConsent, useUserAuthClient, useCoreLogger, useApm } from '#imports'

export const useIntercom = () => {
    const route = useRoute()
    const { intercom, site } = useCoreConfig()
    const { data: user } = useUserInfo()
    const { isConsentReady } = useCookiebotConsent()
    const { client: userAuthClient } = useUserAuthClient()
    const logger = useCoreLogger().withTag('useIntercom')
    const apm = useApm()

    const isBooted = useState<boolean>('intercom:is-booted', () => false)
    const showCookieNotice = useState<boolean>('intercom:show-cookie-notice', () => false)
    const unavailableNoticeVisible = useState<boolean>('intercom:show-unavailable-notice', () => false)
    const hasConsent = useState<boolean>('intercom:has-consent', () => false)

    const isUnavailableNoticeVisible = readonly(unavailableNoticeVisible)

    const isAllowedRoute = computed(() => intercom?.routes?.includes(route.name))

    const isIntercomEnabled = computed(() => {
        if (route.meta.isPlaygame) return false

        return isAllowedRoute.value
    })

    const getIntercomMessengerToken = async () => {
        const { data } = await userAuthClient.GET('/v1/public/intercom/messenger-token')

        if (!data?.token) {
            throw new Error('Unable to get Intercom messenger token.')
        }

        return data
    }

    const reportError = (error: unknown) => {
        logger.error(error)

        if (import.meta.client) {
            apm.captureError(error instanceof Error ? error : new Error(String(error)))
        }
    }

    const showUnavailableNotice = () => {
        unavailableNoticeVisible.value = true
    }

    const hideUnavailableNotice = () => {
        unavailableNoticeVisible.value = false
    }

    const boot = async (): Promise<boolean> => {
        if (isBooted.value) return true
        if (!hasConsent.value) return false
        if (!isIntercomEnabled.value) return false

        const intercomSettings: { app_id: string, [key: string]: any } = {
            api_base: 'https://api-iam.eu.intercom.io',
            app_id: intercom.appId,
            region: 'eu',
            language_override: site.languageShort,
        }

        try {
            // Authenticated users are identified through a signed Intercom JWT.
            if (user.value) {
                const { token } = await getIntercomMessengerToken()
                intercomSettings.intercom_user_jwt = token
            }

            const {
                default: Intercom,
                boot: bootIntercom
            } = await import('@intercom/messenger-js-sdk')

            if (window.Intercom) {
                bootIntercom(intercomSettings)
            }
            else {
                Intercom(intercomSettings)
            }

            isBooted.value = true

            return true
        }
        catch (error) {
            isBooted.value = false
            reportError(error)

            return false
        }
    }

    const showIntercom = async (): Promise<boolean> => {
        if (!isBooted.value) return false

        try {
            const { show } = await import('@intercom/messenger-js-sdk')
            show()

            return true
        }
        catch (error) {
            showUnavailableNotice()
            reportError(error)

            return false
        }
    }

    const updateIntercom = async () => {
        if (!user.value) return

        try {
            const { update } = await import('@intercom/messenger-js-sdk')
            const { token } = await getIntercomMessengerToken()

            update({
                intercom_user_jwt: token,
            })
        }
        catch (error) {
            reportError(error)
        }
    }

    const handleConsentUpdate = async () => {
        hasConsent.value = window.Cookiebot?.consent?.preferences && window.Cookiebot?.consent?.necessary

        if (!hasConsent.value) {
            if (isBooted.value) {
                const { shutdown } = await import('@intercom/messenger-js-sdk')
                shutdown()

                isBooted.value = false
            }

            return
        }

        if (isBooted.value) return

        const didBoot = await boot()

        if (!showCookieNotice.value) return

        showCookieNotice.value = false

        if (!didBoot) {
            showUnavailableNotice()

            return
        }

        await showIntercom()
    }

    if (import.meta.client) {
        useEventListener(window, 'CookiebotOnAccept', handleConsentUpdate)
        useEventListener(window, 'CookiebotOnDecline', handleConsentUpdate)

        watch(isConsentReady, () => {
            handleConsentUpdate()
        }, { immediate: true })
    }

    return {
        isBooted,
        showCookieNotice,
        isUnavailableNoticeVisible,
        showUnavailableNotice,
        hideUnavailableNotice,
        hasConsent,
        isIntercomEnabled,
        boot,
        showIntercom,
        updateIntercom,
    }
}
